// @ts-ignore - Cesium module resolution issue
import * as Cesium from 'cesium';
import { VECTOR_LAYER_TAG, applyTerrainModeToEntity, isTerrainOn } from '../../../utils/vectorLayerTerrainSync';
const flattenPolylineToZeroZ = (entity) => {
    if (!entity.polyline || !entity.polyline.positions || !entity.polyline.positions.getValue)
        return;
    const positions = entity.polyline.positions.getValue();
    if (!positions || !Array.isArray(positions))
        return;
    entity.polyline.positions = positions.map((pos) => {
        const carto = Cesium.Cartographic.fromCartesian(pos);
        return Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, 0);
    });
};
export const loadVectorData = (artifactId, vectorData, metadata, deps) => {
    const { viewer, artifactActions, addStatus } = deps;
    try {
        const kmlBufferSlice = vectorData.buffer.slice(vectorData.byteOffset, vectorData.byteOffset + vectorData.byteLength);
        const kmlBlob = new Blob([kmlBufferSlice], { type: 'application/vnd.google-earth.kml+xml' });
        const kmlBlobUrl = URL.createObjectURL(kmlBlob);
        const dataSource = Cesium.KmlDataSource.load(kmlBlobUrl, {
            camera: viewer.scene.camera,
            canvas: viewer.scene.canvas,
            clampToGround: false
        });
        dataSource.then((dataSourceInstance) => {
            viewer.dataSources.add(dataSourceInstance);
            const terrainOn = isTerrainOn(viewer);
            dataSourceInstance[VECTOR_LAYER_TAG] = true;
            if (dataSourceInstance.entities && dataSourceInstance.entities.values) {
                dataSourceInstance.entities.values.forEach((entity) => {
                    flattenPolylineToZeroZ(entity);
                    if (entity.polygon) {
                        entity.polygon.height = 0;
                        if (entity.polygon.extrudedHeight)
                            entity.polygon.extrudedHeight = undefined;
                    }
                    applyTerrainModeToEntity(entity, terrainOn);
                    if (entity.billboard && !entity.polyline && !entity.polygon) {
                        entity.billboard.show = new Cesium.ConstantProperty(false);
                    }
                    if (entity.point && !entity.polyline && !entity.polygon) {
                        entity.point.show = new Cesium.ConstantProperty(false);
                    }
                });
            }
            artifactActions.updateLoaded(artifactId, {
                type: 'vector',
                cesiumObject: dataSourceInstance,
                metadata: metadata,
                blobUrl: kmlBlobUrl
            });
            artifactActions.resetRetryCount(artifactId);
            artifactActions.initControlsIfAbsent(artifactId, { opacity: 1.0, weight: 1 });
            const waitForRendering = () => {
                if (!dataSourceInstance.isLoading && dataSourceInstance.entities.values.length > 0) {
                    let renderCount = 0;
                    const maxRenderWait = 10;
                    const onPostRender = () => {
                        renderCount++;
                        if (renderCount >= 3) {
                            viewer.scene.postRender.removeEventListener(onPostRender);
                            artifactActions.updateVisibility(artifactId, true);
                            artifactActions.setLoading(artifactId, false);
                        }
                        else if (renderCount >= maxRenderWait) {
                            viewer.scene.postRender.removeEventListener(onPostRender);
                            console.warn(`Vector data ${metadata.getName()} render timeout, proceeding anyway`);
                            artifactActions.updateVisibility(artifactId, true);
                            artifactActions.setLoading(artifactId, false);
                            addStatus(`Loaded vector data (render timeout): ${metadata.getName()}`);
                        }
                    };
                    viewer.scene.postRender.addEventListener(onPostRender);
                }
                else {
                    requestAnimationFrame(waitForRendering);
                }
            };
            waitForRendering();
        }).catch((error) => {
            artifactActions.setLoading(artifactId, false);
            addStatus(`Error loading vector data: ${(error === null || error === void 0 ? void 0 : error.message) || error}`);
            try {
                URL.revokeObjectURL(kmlBlobUrl);
            }
            catch (_a) { }
        });
    }
    catch (error) {
        artifactActions.setLoading(artifactId, false);
        addStatus(`Error processing vector data: ${error}`);
    }
};
export const loadVectorDataFromUrl = (artifactId, url, metadata, deps) => {
    const { viewer, artifactActions, addStatus } = deps;
    try {
        const dataSource = Cesium.KmlDataSource.load(url, {
            camera: viewer.scene.camera,
            canvas: viewer.scene.canvas,
            clampToGround: false
        });
        dataSource.then((dataSourceInstance) => {
            viewer.dataSources.add(dataSourceInstance);
            const terrainOn = isTerrainOn(viewer);
            dataSourceInstance[VECTOR_LAYER_TAG] = true;
            if (dataSourceInstance.entities && dataSourceInstance.entities.values) {
                dataSourceInstance.entities.values.forEach((entity) => {
                    flattenPolylineToZeroZ(entity);
                    if (entity.polygon) {
                        entity.polygon.height = 0;
                        if (entity.polygon.extrudedHeight)
                            entity.polygon.extrudedHeight = undefined;
                    }
                    applyTerrainModeToEntity(entity, terrainOn);
                    if (entity.billboard && !entity.polyline && !entity.polygon) {
                        entity.billboard.show = new Cesium.ConstantProperty(false);
                    }
                    if (entity.point && !entity.polyline && !entity.polygon) {
                        entity.point.show = new Cesium.ConstantProperty(false);
                    }
                });
            }
            artifactActions.updateLoaded(artifactId, {
                type: 'vector',
                cesiumObject: dataSourceInstance,
                metadata: metadata
            });
            artifactActions.resetRetryCount(artifactId);
            artifactActions.initControlsIfAbsent(artifactId, { opacity: 1.0, weight: 1 });
            const waitForRendering = () => {
                if (!dataSourceInstance.isLoading && dataSourceInstance.entities.values.length > 0) {
                    let renderCount = 0;
                    const maxRenderWait = 10;
                    const onPostRender = () => {
                        renderCount++;
                        if (renderCount >= 3) {
                            viewer.scene.postRender.removeEventListener(onPostRender);
                            artifactActions.updateVisibility(artifactId, true);
                            artifactActions.setLoading(artifactId, false);
                        }
                        else if (renderCount >= maxRenderWait) {
                            viewer.scene.postRender.removeEventListener(onPostRender);
                            artifactActions.updateVisibility(artifactId, true);
                            artifactActions.setLoading(artifactId, false);
                            addStatus(`Loaded vector data (render timeout): ${metadata.getName()}`);
                        }
                    };
                    viewer.scene.postRender.addEventListener(onPostRender);
                }
                else {
                    requestAnimationFrame(waitForRendering);
                }
            };
            waitForRendering();
        }).catch((error) => {
            artifactActions.setLoading(artifactId, false);
            addStatus(`Error loading vector data: ${(error === null || error === void 0 ? void 0 : error.message) || error}`);
        });
    }
    catch (error) {
        artifactActions.setLoading(artifactId, false);
        addStatus(`Error processing vector data: ${error}`);
    }
};
